import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getJob,
  listJobChildren,
  getJobArtifacts,
  getJobArtifactUrl,
  getJobArtifactAsText,
  getJobArtifactAsBlob,
  downloadJobArtifactArchive,
  updateJobStatus,
  triggerJob,
  cancelJob,
  deleteJob,
  createScreensIOSStoreUploadJob,
  getMetadataTranslationFailedLocales,
  getLocaleTranslationFailedLocales,
  getRetryableScreenshotUploadFailureCount,
  getScreensUploadFailedRenderLocaleCount,
  canScreensIOSUploadOnly,
  type Job,
  type JobStatus,
  type JobArtifactFolder,
} from '../api/client';
import { Briefcase, ChevronDown, ChevronRight, FileImage, FileText, Folder, FolderOpen, Maximize2, Minimize2, Pause, Play, Trash2, X } from 'lucide-react';
import { useError } from '../context/ErrorContext';
import { panelPath } from '../config/paths';
import { useIsPro } from '../hooks/useIsPro';
import { useDebugMode } from '../hooks/useDebugMode';
import PageBackButton from './PageBackButton';
import ConfirmDialog from './ConfirmDialog';
import ProTag from './ProTag';
import { SkeletonDetail } from './Skeleton';

const JOB_TYPE_KEYS: Record<string, string> = {
  screens_upload: 'panel.jobTypeScreensUpload',
  screens_ai_translation: 'panel.jobTypeScreensAITranslation',
  metadata_export: 'panel.jobTypeMetadataExport',
  metadata_translation: 'panel.jobTypeMetadataTranslation',
  metadata_upload: 'panel.jobTypeMetadataUpload',
  ai_translation: 'panel.jobTypeAITranslation',
  publish: 'panel.jobTypePublish',
  page_site_deploy: 'panel.jobTypePageSiteDeploy',
  revenuecat_ai_translation: 'panel.jobTypeRevenueCatAITranslation',
  revenuecat_upload: 'panel.jobTypeRevenueCatUpload',
  revenuecat_product_sync: 'panel.jobTypeRevenueCatProductSync',
  revenuecat_full_setup: 'panel.jobTypeRevenueCatFullSetup',
  iap_pricing_upload: 'panel.jobTypeIAPPricingUpload',
};

/** Job type'a göre artifact tab etiketi (folder adı boşsa kullanılacak varsayılan). */
function getArtifactTabLabelKey(jobType: string): string {
  switch (jobType) {
    case 'screens_upload':
    case 'metadata_translation':
      return 'panel.jobDetailArtifactsTabScreens';
    case 'ai_translation':
      return 'panel.jobDetailArtifactsTabOutput';
    case 'metadata_upload':
      return 'panel.jobDetailArtifactsTabFiles';
    default:
      return 'panel.jobDetailArtifactsTabScreens';
  }
}

/** Disk/list API artifact'ı olan job tipleri — diğerlerinde /artifacts isteği atılmaz. */
function jobShouldFetchArtifacts(jobType: string, showMetadataPreview: boolean): boolean {
  switch (jobType) {
    case 'screens_upload':
      return true;
    case 'metadata_export':
    case 'metadata_upload':
      return showMetadataPreview;
    default:
      // ai_translation DB'deki aiPartialResults ile gösterilir; publish / metadata_translation vb. artifact üretmez
      return false;
  }
}

/** metadata/android altındaki locale'leri çıkarır (sadece locale adları; sol panel için). */
function jobPayloadHasAppStore(job: Job): boolean {
  const p = job.payload?.platforms;
  return Array.isArray(p) && p.includes('app_store');
}

/** screens_upload restart split-button actions. */
type ScreensRetryAction =
  | 'render_failed'
  | 'render_failed_upload_failed'
  | 'upload_only'
  | 'from_start';

function buildMetadataAndroidLocales(artifactFolders: JobArtifactFolder[]): { locale: string; files: string[] }[] {
  const meta = artifactFolders.find((f) => (f.name || '') === 'metadata');
  if (!meta) return [];
  const byLocale: Record<string, string[]> = {};
  for (const file of meta.files) {
    if (!file.startsWith('android/')) continue;
    const parts = file.split('/');
    if (parts.length < 2) continue;
    const locale = parts[1];
    if (!byLocale[locale]) byLocale[locale] = [];
    byLocale[locale].push(file);
  }
  return Object.entries(byLocale)
    .map(([locale, files]) => ({ locale, files }))
    .sort((a, b) => a.locale.localeCompare(b.locale, undefined, { sensitivity: 'base' }));
}

const STATUS_KEYS: Record<JobStatus, string> = {
  pending: 'panel.statusPending',
  processing: 'panel.statusProcessing',
  paused: 'panel.statusPaused',
  completed: 'panel.statusCompleted',
  failed: 'panel.statusFailed',
};

/** Sub-job / sub-progress UI filter (multi-select). Empty set = show all. */
type SubJobStatusFilter = 'pending' | 'processing' | 'completed' | 'failed';
const SUB_JOB_STATUS_FILTERS: { value: SubJobStatusFilter; labelKey: string; activeClass: string }[] = [
  {
    value: 'processing',
    labelKey: 'panel.jobDetailSubProcessing',
    activeClass: 'bg-blue-100 text-blue-800 ring-1 ring-blue-300 dark:bg-blue-900/40 dark:text-blue-300 dark:ring-blue-700',
  },
  {
    value: 'pending',
    labelKey: 'panel.jobDetailSubPending',
    activeClass: 'bg-gray-200 text-gray-800 ring-1 ring-gray-300 dark:bg-gray-600 dark:text-gray-200 dark:ring-gray-500',
  },
  {
    value: 'completed',
    labelKey: 'panel.jobDetailSubCompleted',
    activeClass: 'bg-green-100 text-green-800 ring-1 ring-green-300 dark:bg-green-900/40 dark:text-green-300 dark:ring-green-700',
  },
  {
    value: 'failed',
    labelKey: 'panel.jobDetailSubFailed',
    activeClass: 'bg-red-100 text-red-800 ring-1 ring-red-300 dark:bg-red-900/40 dark:text-red-300 dark:ring-red-700',
  },
];

function matchesSubJobStatusFilter(status: string, filters: Set<SubJobStatusFilter>): boolean {
  if (filters.size === 0) return true;
  return filters.has(status as SubJobStatusFilter);
}

type ArtifactTreeNode = { name: string; path?: string; children?: ArtifactTreeNode[] };

function buildArtifactTree(files: string[]): ArtifactTreeNode[] {
  type NodeMap = Record<string, { path?: string; children?: NodeMap }>;
  const root: NodeMap = {};
  for (const p of files) {
    const parts = p.split('/');
    let current = root;
    for (let i = 0; i < parts.length; i++) {
      const name = parts[i];
      const isFile = i === parts.length - 1;
      if (!current[name]) {
        current[name] = isFile ? { path: p } : { children: {} };
      } else if (isFile) {
        current[name].path = p;
      } else if (!current[name].children) {
        current[name].children = {};
      }
      current = (current[name].children ?? current) as NodeMap;
    }
  }
  function toList(obj: NodeMap): ArtifactTreeNode[] {
    return Object.entries(obj)
      .map(([k, v]) => ({
        name: k,
        path: v.path,
        children: v.children ? toList(v.children) : undefined,
      }))
      .sort((a, b) => {
        const aIsDir = !a.path;
        const bIsDir = !b.path;
        if (aIsDir !== bIsDir) return aIsDir ? -1 : 1;
        return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
      });
  }
  return toList(root);
}

/** Artifact görselini Authorization ile yükleyip blob URL ile gösterir (img src auth göndermez). */
function ArtifactImage({
  jobId,
  path,
  filename,
  className,
}: {
  jobId: string;
  path: string;
  filename: string;
  className?: string;
}) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const urlRef = useRef<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    getJobArtifactAsBlob(jobId, path)
      .then((blob) => {
        if (cancelled) {
          URL.revokeObjectURL(URL.createObjectURL(blob));
          return;
        }
        const u = URL.createObjectURL(blob);
        urlRef.current = u;
        setBlobUrl(u);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      const u = urlRef.current;
      if (u) {
        URL.revokeObjectURL(u);
        urlRef.current = null;
      }
      setBlobUrl(null);
    };
  }, [jobId, path]);
  if (blobUrl) {
    return <img src={blobUrl} alt={filename} className={className} />;
  }
  return (
    <div className={`bg-gray-200 dark:bg-gray-700 animate-pulse ${className ?? ''}`} title={filename} />
  );
}

/** Tıklanınca büyük ekranda gösterilen artifact görsel lightbox'ı. */
function ArtifactImageLightbox({
  jobId,
  path,
  filename,
  onClose,
}: {
  jobId: string;
  path: string;
  filename: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const urlRef = useRef<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    getJobArtifactAsBlob(jobId, path)
      .then((blob) => {
        if (cancelled) {
          URL.revokeObjectURL(URL.createObjectURL(blob));
          return;
        }
        const u = URL.createObjectURL(blob);
        urlRef.current = u;
        setBlobUrl(u);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      const u = urlRef.current;
      if (u) {
        URL.revokeObjectURL(u);
        urlRef.current = null;
      }
      setBlobUrl(null);
    };
  }, [jobId, path]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={t('panel.jobDetailImagePreview')}
      onClick={onClose}
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
        aria-label={t('common.close')}
      >
        <X className="h-6 w-6" />
      </button>
      <div
        className="relative max-w-[95vw] max-h-[95vh] flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        {blobUrl ? (
          <img
            src={blobUrl}
            alt={filename}
            className="max-w-full max-h-[95vh] w-auto h-auto object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <div className="w-48 h-48 rounded-lg bg-gray-700 animate-pulse" />
        )}
      </div>
    </div>
  );
}

const JobDetail: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [job, setJob] = useState<Job | null>(null);
  const [childJobs, setChildJobs] = useState<Job[]>([]);
  const [artifactFolders, setArtifactFolders] = useState<JobArtifactFolder[]>([]);
  const [artifactFiles, setArtifactFiles] = useState<string[]>([]);
  const [artifactTab, setArtifactTab] = useState<string>('');
  const [selectedArtifactPath, setSelectedArtifactPath] = useState<string | null>(null);
  const [selectedArtifactContent, setSelectedArtifactContent] = useState<string | null>(null);
  const [artifactContentLoading, setArtifactContentLoading] = useState(false);
  const [artifactTreeOpen, setArtifactTreeOpen] = useState<Record<string, boolean>>({});
  const [lightboxImage, setLightboxImage] = useState<{ jobId: string; path: string; filename: string } | null>(null);
  /** Artifacts / output panellerini büyük görünümde açmak için. */
  const [expandedSection, setExpandedSection] = useState<'console' | 'artifacts' | null>(null);
  /** Panel gövdesini (tab içeriğini) kapat/aç. */
  const [sectionOpen, setSectionOpen] = useState<{ console: boolean; artifacts: boolean }>({
    console: true,
    artifacts: true,
  });
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [iosScreensRetrying, setIosScreensRetrying] = useState(false);
  const [iosScreensRetryMenuOpen, setIosScreensRetryMenuOpen] = useState(false);
  const iosScreensRetryMenuRef = useRef<HTMLDivElement>(null);
  const [metadataTranslationRetrying, setMetadataTranslationRetrying] = useState(false);
  const [metadataTranslationRetryMenuOpen, setMetadataTranslationRetryMenuOpen] = useState(false);
  const metadataTranslationRetryMenuRef = useRef<HTMLDivElement>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [subJobStatusFilters, setSubJobStatusFilters] = useState<Set<SubJobStatusFilter>>(() => new Set());
  const logContainerRef = useRef<HTMLDivElement>(null);
  const { showError: showErrorPopup } = useError();
  const { isPro, goToUpgrade } = useIsPro();
  /** Job konsol / metadata önizleme yalnızca DebugMode (API de engeller). */
  const showJobConsole = useDebugMode();
  const showMetadataPreview = showJobConsole;

  // Job konsol çıktısı her güncellendiğinde (ve panel açıldığında) en alta kaydır
  useEffect(() => {
    if (!sectionOpen.console) return;
    const el = logContainerRef.current;
    if (!el) return;
    const scrollToLatest = () => {
      el.scrollTop = el.scrollHeight;
    };
    scrollToLatest();
    const raf = requestAnimationFrame(scrollToLatest);
    return () => cancelAnimationFrame(raf);
  }, [job?.log, sectionOpen.console, expandedSection]);

  // Genişletilmiş panel: Escape ile kapat, body scroll kilidi
  useEffect(() => {
    if (!expandedSection) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setExpandedSection(null);
    };
    window.addEventListener('keydown', handleEscape);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = prevOverflow;
    };
  }, [expandedSection]);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const j = await getJob(id);
      setJob(j);
      if (j.type === 'publish') {
        const children = await listJobChildren(j.id).catch(() => [] as Job[]);
        setChildJobs(children);
      } else {
        setChildJobs([]);
      }
      if (jobShouldFetchArtifacts(j.type, showMetadataPreview)) {
        const artRes = await getJobArtifacts(id).catch(() => ({
          folders: [] as JobArtifactFolder[],
          files: [] as string[],
        }));
        const folders = artRes.folders ?? [];
        const sourceLang = (j.payload?.sourceLanguage as string | undefined) ?? '';
        const sorted = [...folders].sort((a, b) => {
          const aKey = a.name || '';
          const bKey = b.name || '';
          if (sourceLang && aKey === sourceLang) return -1;
          if (sourceLang && bKey === sourceLang) return 1;
          return 0;
        });
        setArtifactFolders(sorted);
        setArtifactFiles(artRes.files ?? []);
        setArtifactTab((prev) => {
          const first = sorted[0] ? (sorted[0].name || '') : '';
          return prev === '' || sorted.some((f: JobArtifactFolder) => (f.name || '') === prev) ? first : prev;
        });
      } else {
        setArtifactFolders([]);
        setArtifactFiles([]);
        setArtifactTab('');
        setSelectedArtifactPath(null);
      }
    } catch (e) {
      showErrorPopup(e);
    } finally {
      setLoading(false);
    }
  }, [id, showErrorPopup, showMetadataPreview]);

  useEffect(() => {
    load();
  }, [load]);

  // screens_upload: metadata/android locale listesi varsa ilk seçimi ilk locale yap (full folder adı yerine)
  useEffect(() => {
    if (artifactFolders.length === 0) return;
    const locales = buildMetadataAndroidLocales(artifactFolders);
    if (locales.length > 0 && !locales.some((e) => e.locale === artifactTab)) {
      setArtifactTab(locales[0].locale);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only sync when folders load, not when user picks a locale
  }, [artifactFolders]);

  // metadata_export / metadata_upload: load selected file content for preview
  useEffect(() => {
    if (
      !id ||
      !job ||
      !showMetadataPreview ||
      (job.type !== 'metadata_export' && job.type !== 'metadata_upload') ||
      !selectedArtifactPath
    ) {
      setSelectedArtifactContent(null);
      return;
    }
    let cancelled = false;
    setArtifactContentLoading(true);
    setSelectedArtifactContent(null);
    getJobArtifactAsText(id, selectedArtifactPath)
      .then((text) => {
        if (!cancelled) setSelectedArtifactContent(text);
      })
      .catch(() => {
        if (!cancelled) setSelectedArtifactContent('');
      })
      .finally(() => {
        if (!cancelled) setArtifactContentLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- job ref intentionally omitted to avoid refetch loops
  }, [id, job?.type, selectedArtifactPath, showMetadataPreview]);

  // metadata_upload: işlem sürerken log için polling
  useEffect(() => {
    if (!id || !job || job.type !== 'metadata_upload' || job.status !== 'processing') return;
    const t = setInterval(() => {
      getJob(id).then((j) => setJob(j)).catch(() => {});
    }, 2000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- job ref intentionally omitted to avoid effect churn
  }, [id, job?.type, job?.status]);

  // publish: grup progress / alt job'lar için polling
  useEffect(() => {
    if (!id || !job || job.type !== 'publish') return;
    if (job.status !== 'processing' && job.status !== 'pending') return;
    const t = setInterval(() => {
      getJob(id)
        .then(async (j) => {
          setJob(j);
          const children = await listJobChildren(j.id).catch(() => [] as Job[]);
          setChildJobs(children);
        })
        .catch(() => {});
    }, 2000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- job ref intentionally omitted to avoid effect churn
  }, [id, job?.type, job?.status]);

  // screens_upload: App Store screenshot yüklemesi (otomatik veya retry) sürerken log / durum için polling
  useEffect(() => {
    if (!id || !job || job.type !== 'screens_upload') return;
    if (job.screenshotUploadStatus !== 'uploading') return;
    const t = setInterval(() => {
      getJob(id).then((j) => setJob(j)).catch(() => {});
    }, 2000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- job ref intentionally omitted to avoid effect churn
  }, [id, job?.type, job?.screenshotUploadStatus]);

  const handleRetryScreensIOSUpload = async (mode: 'full' | 'failed_only' = 'full') => {
    if (!job || job.type !== 'screens_upload' || !canScreensIOSUploadOnly(job)) return;
    setIosScreensRetryMenuOpen(false);
    setIosScreensRetrying(true);
    try {
      const { jobId } = await createScreensIOSStoreUploadJob(job.id, mode);
      // API ebeveyn logunu temizler; worker tetiklenmeden önce detayı yenile.
      await load();
      await triggerJob(jobId);
      await load();
    } catch (e) {
      showErrorPopup(e);
    } finally {
      setIosScreensRetrying(false);
    }
  };

  /** screens_upload restart: render+upload veya yalnızca upload. */
  const handleScreensRetryAction = async (action: ScreensRetryAction) => {
    if (!job || job.type !== 'screens_upload') return;
    if (action === 'upload_only' && !canScreensIOSUploadOnly(job)) return;
    setIosScreensRetryMenuOpen(false);
    if (action === 'from_start') {
      setUpdating(true);
      try {
        await triggerJob(job.id, { mode: 'full' });
        await load();
      } catch (e) {
        showErrorPopup(e);
      } finally {
        setUpdating(false);
      }
      return;
    }
    if (action === 'upload_only') {
      if (!isPro) {
        goToUpgrade();
        return;
      }
      await handleRetryScreensIOSUpload('full');
      return;
    }
    // render_failed | render_failed_upload_failed
    const failedLocales = getScreensUploadFailedRenderLocaleCount(job);
    if (failedLocales === 0) return;
    if (
      action === 'render_failed_upload_failed' &&
      getRetryableScreenshotUploadFailureCount(job) === 0
    ) {
      return;
    }
    setUpdating(true);
    try {
      await triggerJob(job.id, {
        mode:
          action === 'render_failed_upload_failed'
            ? 'render_failed_upload_failed'
            : 'render_failed',
      });
      await load();
    } catch (e) {
      showErrorPopup(e);
    } finally {
      setUpdating(false);
    }
  };

  useEffect(() => {
    if (!iosScreensRetryMenuOpen) return;
    const onDoc = (e: MouseEvent) => {
      const el = iosScreensRetryMenuRef.current;
      if (el && !el.contains(e.target as Node)) {
        setIosScreensRetryMenuOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIosScreensRetryMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('keydown', onKey);
    };
  }, [iosScreensRetryMenuOpen]);

  useEffect(() => {
    if (!metadataTranslationRetryMenuOpen) return;
    const onDoc = (e: MouseEvent) => {
      const el = metadataTranslationRetryMenuRef.current;
      if (el && !el.contains(e.target as Node)) {
        setMetadataTranslationRetryMenuOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMetadataTranslationRetryMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('keydown', onKey);
    };
  }, [metadataTranslationRetryMenuOpen]);

  const handlePause = async () => {
    if (!job || job.status !== 'processing') return;
    setUpdating(true);
    try {
      await updateJobStatus(job.id, { status: 'paused' });
      try {
        await cancelJob(job.id);
      } catch {
        // Status already paused; watcher will cancel if worker call fails.
      }
      await load();
    } catch (e) {
      showErrorPopup(e);
    } finally {
      setUpdating(false);
    }
  };

  const handleStart = async () => {
    const canRun =
      job &&
      (job.status === 'paused' ||
        job.status === 'pending' ||
        job.status === 'completed' ||
        job.status === 'failed');
    if (!canRun) return;
    setUpdating(true);
    try {
      await triggerJob(job.id);
      await load();
    } catch (e) {
      showErrorPopup(e);
    } finally {
      setUpdating(false);
    }
  };

  const handleRetryLocaleTranslation = async (mode: 'full' | 'failed_only') => {
    if (
      !job ||
      (job.type !== 'metadata_translation' && job.type !== 'screens_ai_translation')
    ) {
      return;
    }
    if (mode === 'failed_only' && getLocaleTranslationFailedLocales(job).length === 0) return;
    if (!isPro) {
      goToUpgrade();
      return;
    }
    setMetadataTranslationRetryMenuOpen(false);
    setMetadataTranslationRetrying(true);
    try {
      await triggerJob(job.id, { mode });
      await load();
    } catch (e) {
      showErrorPopup(e);
    } finally {
      setMetadataTranslationRetrying(false);
    }
  };

  if (!id) {
    return (
      <div className="p-4 space-y-4">
        <PageBackButton to={panelPath('/jobs')} />
        <p className="text-gray-500 dark:text-gray-400">{t('panel.jobIdRequired')}</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-4">
        <div className="max-w-5xl mx-auto space-y-4">
          <PageBackButton to={panelPath('/jobs')} />
          <SkeletonDetail />
        </div>
      </div>
    );
  }
  if (!job) {
    return (
      <div className="p-4">
        <div className="max-w-5xl mx-auto space-y-4">
          <PageBackButton to={panelPath('/jobs')} />
          <div className="py-12 text-center text-gray-500 dark:text-gray-400">
            {t('panel.jobNotFound')}
          </div>
        </div>
      </div>
    );
  }

  const screensStoreUploading =
    job.type === 'screens_upload' && job.screenshotUploadStatus === 'uploading';
  /** Store upload sürerken progress bar completed zorlamasın; gerçek % (genelde 85) göster. */
  const displayProgress =
    job.status === 'completed' && !screensStoreUploading ? 100 : (job.progress ?? 0);
  const displayStatus = screensStoreUploading ? 'processing' : job.status;
  const canPause = job.status === 'processing' && !screensStoreUploading;
  const canStart =
    job.status === 'paused' ||
    job.status === 'pending' ||
    job.status === 'completed' ||
    job.status === 'failed';
  const isRerun = job.status === 'completed' || job.status === 'failed';
  /** screens_upload completed/failed + App Store: Restart menüsünde store upload seçenekleri. */
  const showScreensRetryMenu =
    job.type === 'screens_upload' &&
    (job.status === 'completed' || job.status === 'failed') &&
    !screensStoreUploading &&
    jobPayloadHasAppStore(job);
  const screensRetryBusy =
    iosScreensRetrying ||
    updating ||
    screensStoreUploading;
  const screensFailedLocaleCount = showScreensRetryMenu
    ? getScreensUploadFailedRenderLocaleCount(job)
    : 0;
  const screensFailedUploadCount = showScreensRetryMenu
    ? getRetryableScreenshotUploadFailureCount(job)
    : 0;
  const screensCanUploadOnly = showScreensRetryMenu && canScreensIOSUploadOnly(job);
  /** Ana Restart aksiyonu: render bitti → upload only; failed → partial retry > from start. */
  const screensRetryDefaultAction: ScreensRetryAction = (() => {
    if (!showScreensRetryMenu) return 'from_start';
    // completed veya render success + upload fail → varsayılan yalnızca store upload.
    if (screensCanUploadOnly) return 'upload_only';
    if (screensFailedLocaleCount > 0 && screensFailedUploadCount > 0) {
      return 'render_failed_upload_failed';
    }
    if (screensFailedLocaleCount > 0) return 'render_failed';
    return 'from_start';
  })();
  const jobTypeLabel = t(JOB_TYPE_KEYS[job.type] ?? job.type);
  const localeTranslationFailedLocales =
    job.type === 'metadata_translation' || job.type === 'screens_ai_translation'
      ? getLocaleTranslationFailedLocales(job)
      : [];
  /** metadata_translation / screens_ai_translation completed/failed: Restart split (failed_only > full). */
  const showLocaleTranslationRetryMenu =
    (job.type === 'metadata_translation' || job.type === 'screens_ai_translation') &&
    (job.status === 'completed' || job.status === 'failed');
  const metadataTranslationRetryBusy = metadataTranslationRetrying || updating;
  const localeTranslationRetryDefaultAction: 'failed_only' | 'full' =
    localeTranslationFailedLocales.length > 0 ? 'failed_only' : 'full';

  const toggleSubJobStatusFilter = (value: SubJobStatusFilter) => {
    setSubJobStatusFilters((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  };

  const filteredSubProgress = (job.subProgress ?? []).filter((sub) =>
    matchesSubJobStatusFilter(sub.status, subJobStatusFilters)
  );
  const filteredChildJobs = childJobs.filter((child) =>
    matchesSubJobStatusFilter(child.status, subJobStatusFilters)
  );

  const renderSubJobStatusFilters = () => (
    <div
      className="flex flex-wrap items-center gap-1.5"
      role="group"
      aria-label={t('panel.jobDetailSubStatusFilter')}
    >
      {SUB_JOB_STATUS_FILTERS.map(({ value, labelKey, activeClass }) => {
        const active = subJobStatusFilters.has(value);
        return (
          <button
            key={value}
            type="button"
            aria-pressed={active}
            onClick={() => toggleSubJobStatusFilter(value)}
            className={`px-2 py-0.5 rounded text-xs font-medium transition-colors ${
              active
                ? activeClass
                : 'bg-gray-50 text-gray-500 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700'
            }`}
          >
            {t(labelKey)}
          </button>
        );
      })}
    </div>
  );

  const handleDelete = () => {
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!job) return;
    setDeleting(true);
    try {
      await deleteJob(job.id);
      setDeleteConfirmOpen(false);
      navigate(panelPath('/jobs'));
    } catch (e) {
      showErrorPopup(e);
    } finally {
      setDeleting(false);
    }
  };

  const consoleExpanded = expandedSection === 'console';
  const artifactsExpanded = expandedSection === 'artifacts';
  const consoleOpen = sectionOpen.console;
  const artifactsOpen = sectionOpen.artifacts;
  const sectionShellClass = (expanded: boolean) =>
    expanded
      ? 'fixed inset-3 sm:inset-6 z-40 flex flex-col rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-2xl overflow-hidden'
      : 'rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden';
  const toggleSectionOpen = (section: 'console' | 'artifacts') => {
    setSectionOpen((prev) => {
      const nextOpen = !prev[section];
      if (!nextOpen && expandedSection === section) {
        setExpandedSection(null);
      }
      return { ...prev, [section]: nextOpen };
    });
  };
  const renderSectionHeaderToggle = (section: 'console' | 'artifacts') => {
    const open = sectionOpen[section];
    return (
      <button
        type="button"
        onClick={() => toggleSectionOpen(section)}
        className="inline-flex items-center justify-center p-0.5 rounded text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
        title={open ? t('panel.collapse') : t('panel.expand')}
        aria-label={open ? t('panel.collapse') : t('panel.expand')}
        aria-expanded={open}
      >
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
      </button>
    );
  };
  const renderExpandToggle = (section: 'console' | 'artifacts') => {
    const expanded = expandedSection === section;
    const open = sectionOpen[section];
    return (
      <button
        type="button"
        onClick={() => {
          if (!open) {
            setSectionOpen((prev) => ({ ...prev, [section]: true }));
          }
          setExpandedSection(expanded ? null : section);
        }}
        className="inline-flex items-center gap-1.5 p-1.5 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
        title={expanded ? t('panel.collapse') : t('panel.expand')}
        aria-label={expanded ? t('panel.collapse') : t('panel.expand')}
        aria-pressed={expanded}
      >
        {expanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
      </button>
    );
  };

  return (
    <>
    {expandedSection && (
      <div
        className="fixed inset-0 z-30 bg-black/50"
        aria-hidden="true"
        onClick={() => setExpandedSection(null)}
      />
    )}
    <div className="p-4">
      <div className="max-w-5xl mx-auto space-y-6">
      <div className="space-y-2">
        <PageBackButton to={panelPath('/jobs')} />
        <h1 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <Briefcase className="h-6 w-6 text-primary-500 dark:text-primary-400" />
          {t('panel.jobDetailTitle')}
        </h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
          {jobTypeLabel} · {job.id}
        </p>
      </div>

      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            {t('panel.jobDetailInfo')}
          </h2>
          <div className="flex flex-wrap items-center justify-end gap-2 ml-auto">
            {canPause && (
              <button
                type="button"
                onClick={handlePause}
                disabled={updating}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-900/30 hover:bg-amber-200 dark:hover:bg-amber-800/40 disabled:opacity-50"
              >
                <Pause className="h-4 w-4" />
                {t('panel.pause')}
              </button>
            )}
            {canStart &&
              (showScreensRetryMenu ? (
                <div className="relative inline-flex" ref={iosScreensRetryMenuRef}>
                  <button
                    type="button"
                    onClick={() => {
                      if (screensRetryDefaultAction === 'from_start') {
                        void handleScreensRetryAction('from_start');
                        return;
                      }
                      if (
                        screensRetryDefaultAction === 'upload_only' ||
                        screensRetryDefaultAction === 'render_failed' ||
                        screensRetryDefaultAction === 'render_failed_upload_failed'
                      ) {
                        if (!isPro && screensRetryDefaultAction === 'upload_only') {
                          goToUpgrade();
                          return;
                        }
                        void handleScreensRetryAction(screensRetryDefaultAction);
                        return;
                      }
                    }}
                    disabled={screensRetryBusy}
                    className={
                      isPro ||
                      screensRetryDefaultAction === 'from_start' ||
                      screensRetryDefaultAction === 'render_failed' ||
                      screensRetryDefaultAction === 'render_failed_upload_failed'
                        ? 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-l-lg text-sm font-medium text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-900/30 hover:bg-green-200 dark:hover:bg-green-800/40 disabled:opacity-50'
                        : 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-l-lg text-sm font-medium bg-gray-200 text-gray-500 dark:bg-gray-600 dark:text-gray-400 cursor-pointer'
                    }
                    title={
                      screensRetryDefaultAction === 'render_failed_upload_failed'
                        ? t('panel.jobDetailRetryScreensRenderFailedUploadFailed')
                        : screensRetryDefaultAction === 'render_failed'
                          ? t('panel.jobDetailRetryScreensRenderFailedUpload')
                          : screensRetryDefaultAction === 'upload_only'
                            ? t('panel.jobDetailRetryIOSScreensFull')
                            : t('panel.jobDetailRetryJobFromStart')
                    }
                  >
                    <Play className="h-4 w-4" />
                    {iosScreensRetrying || updating
                      ? t('panel.jobDetailScreensIOSRetryStarting')
                      : screensRetryDefaultAction === 'render_failed_upload_failed'
                        ? t('panel.jobDetailRetryScreensRenderFailedUploadFailed')
                        : screensRetryDefaultAction === 'render_failed'
                          ? t('panel.jobDetailRetryScreensRenderFailedUpload')
                          : screensRetryDefaultAction === 'upload_only'
                            ? t('panel.jobDetailRetryIOSScreensFull')
                            : t('panel.jobDetailRetryJobFromStart')}
                    {!isPro && screensRetryDefaultAction === 'upload_only' && <ProTag />}
                  </button>
                  <button
                    type="button"
                    onClick={() => setIosScreensRetryMenuOpen((o) => !o)}
                    disabled={screensRetryBusy}
                    className="inline-flex items-center px-2 py-1.5 rounded-r-lg border-l border-green-300/60 dark:border-green-700/60 text-sm font-medium text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-900/30 hover:bg-green-200 dark:hover:bg-green-800/40 disabled:opacity-50"
                    title={t('panel.restart')}
                    aria-expanded={iosScreensRetryMenuOpen}
                    aria-haspopup="menu"
                  >
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${iosScreensRetryMenuOpen ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {iosScreensRetryMenuOpen && (
                    <div
                      role="menu"
                      className="absolute right-0 top-full mt-1 z-20 min-w-[280px] rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg py-1"
                    >
                      <button
                        type="button"
                        role="menuitem"
                        disabled={!screensCanUploadOnly || screensRetryBusy}
                        className={`w-full text-left px-3 py-2 text-sm text-gray-800 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:pointer-events-none ${
                          screensRetryDefaultAction === 'upload_only' ? 'bg-gray-50 dark:bg-gray-700/50' : ''
                        }`}
                        onClick={() => {
                          if (!screensCanUploadOnly) return;
                          if (!isPro) {
                            setIosScreensRetryMenuOpen(false);
                            goToUpgrade();
                            return;
                          }
                          void handleScreensRetryAction('upload_only');
                        }}
                      >
                        <span className="flex items-center gap-1.5">
                          <span className="block">{t('panel.jobDetailRetryIOSScreensFull')}</span>
                          {!isPro && <ProTag />}
                        </span>
                        <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          {t('panel.jobDetailRetryIOSScreensFullHint')}
                        </span>
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        disabled={screensRetryBusy}
                        className={`w-full text-left px-3 py-2 text-sm text-gray-800 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:pointer-events-none ${
                          screensRetryDefaultAction === 'from_start' ? 'bg-gray-50 dark:bg-gray-700/50' : ''
                        }`}
                        onClick={() => {
                          void handleScreensRetryAction('from_start');
                        }}
                      >
                        <span className="block">{t('panel.jobDetailRetryJobFromStart')}</span>
                        <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          {t('panel.jobDetailRetryJobFromStartHint')}
                        </span>
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        disabled={screensFailedLocaleCount === 0 || screensRetryBusy}
                        className={`w-full text-left px-3 py-2 text-sm text-gray-800 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:pointer-events-none border-t border-gray-100 dark:border-gray-700 ${
                          screensRetryDefaultAction === 'render_failed' ? 'bg-gray-50 dark:bg-gray-700/50' : ''
                        }`}
                        onClick={() => {
                          if (screensFailedLocaleCount === 0) return;
                          void handleScreensRetryAction('render_failed');
                        }}
                      >
                        <span className="block">{t('panel.jobDetailRetryScreensRenderFailedUpload')}</span>
                        <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          {screensFailedLocaleCount === 0
                            ? t('panel.jobDetailRetryIOSScreensNoFailures')
                            : t('panel.jobDetailRetryScreensRenderFailedUploadHint', {
                                count: screensFailedLocaleCount,
                              })}
                        </span>
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        disabled={
                          screensFailedLocaleCount === 0 ||
                          screensFailedUploadCount === 0 ||
                          screensRetryBusy
                        }
                        className={`w-full text-left px-3 py-2 text-sm text-gray-800 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:pointer-events-none ${
                          screensRetryDefaultAction === 'render_failed_upload_failed'
                            ? 'bg-gray-50 dark:bg-gray-700/50'
                            : ''
                        }`}
                        onClick={() => {
                          if (screensFailedLocaleCount === 0 || screensFailedUploadCount === 0) return;
                          void handleScreensRetryAction('render_failed_upload_failed');
                        }}
                      >
                        <span className="block">
                          {t('panel.jobDetailRetryScreensRenderFailedUploadFailed')}
                        </span>
                        <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          {screensFailedLocaleCount === 0 || screensFailedUploadCount === 0
                            ? t('panel.jobDetailRetryIOSScreensNoFailures')
                            : t('panel.jobDetailRetryScreensRenderFailedUploadFailedHint', {
                                count: screensFailedUploadCount,
                              })}
                        </span>
                      </button>
                    </div>
                  )}
                </div>
              ) : showLocaleTranslationRetryMenu ? (
                <div className="relative inline-flex" ref={metadataTranslationRetryMenuRef}>
                  <button
                    type="button"
                    onClick={() => {
                      if (!isPro) {
                        goToUpgrade();
                        return;
                      }
                      void handleRetryLocaleTranslation(localeTranslationRetryDefaultAction);
                    }}
                    disabled={metadataTranslationRetryBusy}
                    className={
                      isPro
                        ? 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-l-lg text-sm font-medium text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-900/30 hover:bg-green-200 dark:hover:bg-green-800/40 disabled:opacity-50'
                        : 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-l-lg text-sm font-medium bg-gray-200 text-gray-500 dark:bg-gray-600 dark:text-gray-400 cursor-pointer'
                    }
                    title={
                      localeTranslationRetryDefaultAction === 'failed_only'
                        ? t('panel.jobDetailMetadataTranslationRetryFailed')
                        : t('panel.jobDetailMetadataTranslationRetryFull')
                    }
                  >
                    <Play className="h-4 w-4" />
                    {metadataTranslationRetrying
                      ? t('panel.jobDetailMetadataTranslationRetryStarting')
                      : localeTranslationRetryDefaultAction === 'failed_only'
                        ? t('panel.jobDetailMetadataTranslationRetryFailed')
                        : t('panel.jobDetailMetadataTranslationRetryFull')}
                    {!isPro && <ProTag />}
                  </button>
                  <button
                    type="button"
                    onClick={() => setMetadataTranslationRetryMenuOpen((o) => !o)}
                    disabled={metadataTranslationRetryBusy}
                    className="inline-flex items-center px-2 py-1.5 rounded-r-lg border-l border-green-300/60 dark:border-green-700/60 text-sm font-medium text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-900/30 hover:bg-green-200 dark:hover:bg-green-800/40 disabled:opacity-50"
                    title={t('panel.restart')}
                    aria-expanded={metadataTranslationRetryMenuOpen}
                    aria-haspopup="menu"
                  >
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${metadataTranslationRetryMenuOpen ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {metadataTranslationRetryMenuOpen && (
                    <div
                      role="menu"
                      className="absolute right-0 top-full mt-1 z-20 min-w-[260px] rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg py-1"
                    >
                      {(() => {
                        const failedCount = localeTranslationFailedLocales.length;
                        const noFailures = failedCount === 0;
                        return (
                          <button
                            type="button"
                            role="menuitem"
                            disabled={noFailures || metadataTranslationRetryBusy}
                            className={`w-full text-left px-3 py-2 text-sm text-gray-800 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:pointer-events-none ${
                              localeTranslationRetryDefaultAction === 'failed_only'
                                ? 'bg-gray-50 dark:bg-gray-700/50'
                                : ''
                            }`}
                            onClick={() => {
                              if (noFailures) return;
                              if (!isPro) {
                                setMetadataTranslationRetryMenuOpen(false);
                                goToUpgrade();
                                return;
                              }
                              void handleRetryLocaleTranslation('failed_only');
                            }}
                          >
                            <span className="flex items-center gap-1.5">
                              <span className="block">
                                {t('panel.jobDetailMetadataTranslationRetryFailed')}
                              </span>
                              {!isPro && <ProTag />}
                            </span>
                            <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                              {noFailures
                                ? t('panel.jobDetailMetadataTranslationRetryNoFailures')
                                : t('panel.jobDetailMetadataTranslationRetryFailedHint', {
                                    count: failedCount,
                                    locales: localeTranslationFailedLocales.join(', '),
                                  })}
                            </span>
                          </button>
                        );
                      })()}
                      <button
                        type="button"
                        role="menuitem"
                        disabled={metadataTranslationRetryBusy}
                        className={`w-full text-left px-3 py-2 text-sm text-gray-800 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:pointer-events-none ${
                          localeTranslationRetryDefaultAction === 'full'
                            ? 'bg-gray-50 dark:bg-gray-700/50'
                            : ''
                        }`}
                        onClick={() => {
                          if (!isPro) {
                            setMetadataTranslationRetryMenuOpen(false);
                            goToUpgrade();
                            return;
                          }
                          void handleRetryLocaleTranslation('full');
                        }}
                      >
                        <span className="flex items-center gap-1.5">
                          <span className="block">{t('panel.jobDetailMetadataTranslationRetryFull')}</span>
                          {!isPro && <ProTag />}
                        </span>
                        <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          {t('panel.jobDetailMetadataTranslationRetryFullHint')}
                        </span>
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <button
                  type="button"
                  onClick={handleStart}
                  disabled={updating}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-900/30 hover:bg-green-200 dark:hover:bg-green-800/40 disabled:opacity-50"
                  title={isRerun ? t('panel.restart') : t('panel.start')}
                >
                  <Play className="h-4 w-4" />
                  {isRerun ? t('panel.restart') : t('panel.start')}
                </button>
              ))}
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleting}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 disabled:opacity-50"
              title={t('panel.jobDeleteTitle')}
            >
              <Trash2 className="h-4 w-4" />
              {deleting ? t('panel.deleting') : t('common.delete')}
            </button>
          </div>
        </div>
        <dl className="p-4 grid gap-3 sm:grid-cols-2">
          <div>
            <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {t('panel.type')}
            </dt>
            <dd className="mt-0.5 text-sm font-medium text-gray-900 dark:text-white">
              {jobTypeLabel}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {t('panel.status')}
            </dt>
            <dd className="mt-0.5">
              <span
                className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                  displayStatus === 'pending'
                    ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                    : displayStatus === 'processing'
                      ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                      : displayStatus === 'paused'
                        ? 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                        : displayStatus === 'completed'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                          : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                }`}
              >
                {t(STATUS_KEYS[displayStatus] ?? STATUS_KEYS[job.status])}
              </span>
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {t('panel.jobDetailApplication')}
            </dt>
            <dd className="mt-0.5 text-sm text-gray-900 dark:text-white">
              {job.applicationId ? (
                <Link
                  to={panelPath(`/applications/${job.applicationId}`)}
                  className="text-primary-600 dark:text-primary-400 hover:underline"
                >
                  {job.applicationName || job.applicationId}
                </Link>
              ) : (
                '—'
              )}
            </dd>
          </div>
          {job.type === 'metadata_translation' && (job.payload as { versionName?: string })?.versionName != null ? (
            <div>
              <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {t('panel.jobDetailVersion')}
              </dt>
              <dd className="mt-0.5 text-sm text-gray-900 dark:text-white">
                {(job.payload as { versionName: string }).versionName}
              </dd>
            </div>
          ) : job.type === 'screens_upload' && (job.payload?.sourceLanguage != null || (job.payload?.targetLocales as string[] | undefined)?.length) ? (
            <>
              {job.payload?.sourceLanguage != null && (
                <div>
                  <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('panel.jobDetailSourceLanguage')}
                  </dt>
                  <dd className="mt-0.5 text-sm text-gray-900 dark:text-white">
                    {String(job.payload.sourceLanguage)}
                  </dd>
                </div>
              )}
              {Array.isArray(job.payload?.targetLocales) && (job.payload.targetLocales as string[]).length > 0 && (
                <div>
                  <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('panel.jobDetailTargetLanguages')}
                  </dt>
                  <dd className="mt-0.5 text-sm text-gray-900 dark:text-white">
                    {(job.payload.targetLocales as string[]).join(', ')}
                  </dd>
                </div>
              )}
            </>
          ) : null}
          <div>
            <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {t('panel.jobDetailCreatedUpdated')}
            </dt>
            <dd className="mt-0.5 text-sm text-gray-600 dark:text-gray-400">
              {new Date(job.createdAt).toLocaleString('tr-TR')} /{' '}
              {new Date(job.updatedAt).toLocaleString('tr-TR')}
            </dd>
          </div>
          {job.parentJobId && (
            <div>
              <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {t('panel.jobDetailPartOfPublish')}
              </dt>
              <dd className="mt-0.5 text-sm">
                <Link
                  to={panelPath(`/jobs/${job.parentJobId}`)}
                  className="text-primary-600 dark:text-primary-400 hover:underline font-medium"
                >
                  {t('panel.jobTypePublish')}
                </Link>
              </dd>
            </div>
          )}
          {job.warnings && job.warnings.length > 0 && (
            <div className="sm:col-span-2">
              <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {t('panel.jobDetailWarnings')}
              </dt>
              <dd className="mt-1.5 space-y-2">
                {job.warnings.map(w => (
                  <div
                    key={w.id}
                    className="text-sm text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-900/20 rounded-lg p-2"
                  >
                    {w.message}
                    {w.locale ? (
                      <span className="ml-2 text-xs font-mono text-amber-700/80 dark:text-amber-300/80">
                        {w.locale}
                        {w.field ? ` · ${w.field}` : ''}
                      </span>
                    ) : null}
                  </div>
                ))}
              </dd>
            </div>
          )}
          {job.errorMessage && (
            <div className="sm:col-span-2">
              <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {t('panel.jobDetailErrorMessage')}
              </dt>
              <dd className="mt-0.5 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg p-2">
                {job.errorMessage}
              </dd>
            </div>
          )}
          {showJobConsole && job.errorDebug && (
            <div className="sm:col-span-2">
              <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {t('panel.jobDetailErrorDebug')}
              </dt>
              <dd className="mt-0.5 text-xs font-mono text-red-800 dark:text-red-300 bg-red-50 dark:bg-red-900/20 rounded-lg p-2 whitespace-pre-wrap break-words max-h-64 overflow-y-auto">
                {job.errorDebug}
              </dd>
            </div>
          )}
        </dl>
      </div>

      {job.aiCredits && job.aiCredits.creditsSpent ? (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              {t('credits.jobSummary')}
            </h2>
          </div>
          <dl className="p-4 grid grid-cols-2 gap-4 text-sm">
            <div>
              <dt className="text-xs text-gray-500 dark:text-gray-400">{t('credits.spent')}</dt>
              <dd className="mt-0.5 font-medium tabular-nums text-gray-900 dark:text-white">
                {(job.aiCredits.creditsSpent ?? 0).toLocaleString()}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500 dark:text-gray-400">{t('credits.colIp')}</dt>
              <dd className="mt-0.5 font-mono text-xs text-gray-700 dark:text-gray-300 truncate" title={job.aiCredits.clientIp}>
                {job.aiCredits.clientIp || '—'}
              </dd>
            </div>
          </dl>
        </div>
      ) : null}

      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            {t('panel.progress')}
          </h2>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <div className="h-4 rounded-full bg-gray-200 dark:bg-gray-600 overflow-hidden">
              <div
                className="h-full bg-primary-500 dark:bg-primary-400 rounded-full transition-all"
                style={{ width: `${displayProgress}%` }}
              />
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {t('panel.jobDetailProgressCompleted', { percent: displayProgress })}
            </p>
          </div>
          {job.subProgress && job.subProgress.length > 0 && (
            <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('panel.jobDetailSubOperations')}
                </p>
                {renderSubJobStatusFilters()}
              </div>
              {filteredSubProgress.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 py-1">
                  {t('panel.jobDetailSubStatusFilterEmpty')}
                </p>
              ) : (
              <ul className="space-y-2 max-h-48 overflow-y-auto pr-1">
                {filteredSubProgress.map((sub) => {
                  const statusClass =
                    sub.status === 'completed'
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                      : sub.status === 'failed'
                        ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                        : sub.status === 'processing'
                          ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                          : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
                  const subStatusLabel =
                    sub.status === 'processing'
                      ? t('panel.jobDetailSubProcessing')
                      : sub.status === 'completed'
                        ? t('panel.jobDetailSubCompleted')
                        : sub.status === 'failed'
                          ? t('panel.jobDetailSubFailed')
                          : t('panel.jobDetailSubPending');
                  const childMatch = childJobs.find(c => c.type === sub.label);
                  const labelText = JOB_TYPE_KEYS[sub.label]
                    ? t(JOB_TYPE_KEYS[sub.label])
                    : (sub.label || '—');
                  return (
                    <li key={sub.label} className="flex items-center gap-3">
                      <span className="min-w-[7rem] max-w-[12rem] text-sm font-medium text-gray-700 dark:text-gray-300 shrink-0 truncate">
                        {childMatch ? (
                          <Link
                            to={panelPath(`/jobs/${childMatch.id}`)}
                            className="text-primary-600 dark:text-primary-400 hover:underline"
                          >
                            {labelText}
                          </Link>
                        ) : (
                          labelText
                        )}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="h-2 rounded-full bg-gray-200 dark:bg-gray-600 overflow-hidden">
                          <div
                            className="h-full bg-primary-500 dark:bg-primary-400 rounded-full transition-all"
                            style={{ width: `${sub.progress ?? 0}%` }}
                          />
                        </div>
                      </div>
                      <span
                        className={`shrink-0 px-2 py-0.5 rounded text-xs font-medium ${statusClass}`}
                      >
                        {subStatusLabel}
                      </span>
                    </li>
                  );
                })}
              </ul>
              )}
            </div>
          )}
        </div>
      </div>

      {job.type === 'publish' && childJobs.length > 0 && (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              {t('panel.jobDetailSubJobs')}
            </h2>
            {renderSubJobStatusFilters()}
          </div>
          {filteredChildJobs.length === 0 ? (
            <p className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
              {t('panel.jobDetailSubStatusFilterEmpty')}
            </p>
          ) : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-700/50">
            {filteredChildJobs.map(child => (
              <li key={child.id}>
                <Link
                  to={panelPath(`/jobs/${child.id}`)}
                  className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-gray-50/80 dark:hover:bg-gray-700/30"
                >
                  <span className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-900 dark:text-white">
                    <Briefcase className="h-3.5 w-3.5 text-gray-500" />
                    {t(JOB_TYPE_KEYS[child.type] ?? child.type)}
                  </span>
                  <span className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                    <span
                      className={`inline-flex px-2 py-0.5 rounded font-medium ${
                        child.status === 'completed'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                          : child.status === 'failed'
                            ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                            : child.status === 'processing'
                              ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                              : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                      }`}
                    >
                      {t(STATUS_KEYS[child.status] ?? child.status)}
                    </span>
                    <span>{child.progress ?? 0}%</span>
                    <ChevronRight className="h-4 w-4" />
                  </span>
                </Link>
              </li>
            ))}
          </ul>
          )}
        </div>
      )}

      {job.type === 'metadata_upload' && showJobConsole && (
        <div className={sectionShellClass(consoleExpanded && consoleOpen)}>
          <div className={`p-4 flex items-center gap-2 flex-wrap shrink-0 ${consoleOpen ? 'border-b border-gray-200 dark:border-gray-700' : ''}`}>
            {renderSectionHeaderToggle('console')}
            <FileText className="h-4 w-4 text-gray-500 dark:text-gray-400 shrink-0" />
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              {t('panel.jobDetailFastlaneConsole')}
            </h2>
            {job.status === 'processing' && (
              <span className="text-xs text-gray-500 dark:text-gray-400">{t('panel.jobDetailLiveUpdating')}</span>
            )}
            <div className="ml-auto flex items-center gap-2">
              {(job.payload as { exportJobId?: string })?.exportJobId && (
                <Link
                  to={panelPath(`/jobs/${(job.payload as { exportJobId: string }).exportJobId}`)}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/30"
                >
                  <FolderOpen className="h-4 w-4" />
                  {t('panel.jobDetailGoToExport')}
                </Link>
              )}
              {consoleOpen && renderExpandToggle('console')}
            </div>
          </div>
          {consoleOpen && (
            <div
              ref={logContainerRef}
              className={`p-4 overflow-auto ${consoleExpanded ? 'flex-1 min-h-0' : 'max-h-[480px]'}`}
            >
              <pre className="font-mono text-xs text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-words bg-gray-50 dark:bg-gray-900 rounded-lg p-4 min-h-[120px]">
                {job.log ?? t('panel.jobDetailOutputWaiting')}
              </pre>
            </div>
          )}
        </div>
      )}

      {job.type === 'screens_upload' &&
        ((showJobConsole &&
          (Boolean(job.log) ||
            Boolean(job.screenshotUploadStatus) ||
            Boolean(job.screenshotUploadError) ||
            (job.screenshotUploadFailures?.length ?? 0) > 0)) ||
          (!showJobConsole &&
            (Boolean(job.screenshotUploadError) ||
              (job.screenshotUploadFailures?.length ?? 0) > 0))) && (
        <div className={sectionShellClass(consoleExpanded && consoleOpen)}>
          {showJobConsole && (
            <div className={`p-4 flex items-center gap-2 flex-wrap shrink-0 ${consoleOpen ? 'border-b border-gray-200 dark:border-gray-700' : ''}`}>
              {renderSectionHeaderToggle('console')}
              <FileText className="h-4 w-4 text-gray-500 dark:text-gray-400 shrink-0" />
              <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                {t('panel.jobDetailScreensIOSFastlaneConsole')}
              </h2>
              {job.screenshotUploadStatus === 'uploading' && (
                <span className="text-xs text-gray-500 dark:text-gray-400">{t('panel.jobDetailLiveUpdating')}</span>
              )}
              <div className="ml-auto">{consoleOpen && renderExpandToggle('console')}</div>
            </div>
          )}
          {consoleOpen && (
            <div
              ref={logContainerRef}
              className={`p-4 overflow-auto ${consoleExpanded ? 'flex-1 min-h-0' : 'max-h-[480px]'}`}
            >
              {job.screenshotUploadError ? (
                <p className="text-sm text-red-600 dark:text-red-400 mb-3 whitespace-pre-wrap break-words">
                  {job.screenshotUploadError}
                </p>
              ) : null}
              {job.screenshotUploadFailures && job.screenshotUploadFailures.length > 0 ? (
                <div className="mb-3 rounded-lg border border-amber-200 dark:border-amber-800/60 bg-amber-50 dark:bg-amber-950/30 px-3 py-2">
                  <p className="text-xs font-semibold text-amber-800 dark:text-amber-200 mb-1.5">
                    {t('panel.jobDetailScreenshotFailures')}
                  </p>
                  <ul className="space-y-1">
                    {job.screenshotUploadFailures.map((f) => (
                      <li
                        key={`${f.locale}/${f.file}`}
                        className="text-xs text-amber-900 dark:text-amber-100 font-mono flex flex-wrap items-center gap-x-2 gap-y-0.5"
                      >
                        <span>
                          {f.locale}/{f.file}
                        </span>
                        <span className="text-amber-700/80 dark:text-amber-300/80">
                          {t('panel.jobDetailScreenshotFailureAttempts', { count: f.attempts })}
                        </span>
                        {f.skipped ? (
                          <span className="text-amber-700 dark:text-amber-300">
                            ({t('panel.jobDetailScreenshotFailureSkipped')})
                          </span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {showJobConsole && (
                <pre className="font-mono text-xs text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-words bg-gray-50 dark:bg-gray-900 rounded-lg p-4 min-h-[120px]">
                  {job.log ?? t('panel.jobDetailOutputWaiting')}
                </pre>
              )}
            </div>
          )}
        </div>
      )}

      {showMetadataPreview &&
        (job.type === 'metadata_export' || job.type === 'metadata_upload') &&
        job.status === 'completed' &&
        artifactFiles.length > 0 && (
        <div className={sectionShellClass(artifactsExpanded && artifactsOpen)}>
          <div className={`p-4 flex flex-wrap items-center gap-2 shrink-0 ${artifactsOpen ? 'border-b border-gray-200 dark:border-gray-700' : ''}`}>
            {renderSectionHeaderToggle('artifacts')}
            <FileText className="h-4 w-4 text-gray-500 dark:text-gray-400 shrink-0" />
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              {t('panel.jobDetailMetadataPreview')}
            </h2>
            <div className="ml-auto flex items-center gap-2">
              <button
                type="button"
                onClick={() => downloadJobArtifactArchive(job.id)}
                className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
              >
                {t('panel.jobDetailDownloadZip')}
              </button>
              {artifactsOpen && renderExpandToggle('artifacts')}
            </div>
          </div>
          {artifactsOpen && (
          <div className={`flex ${artifactsExpanded ? 'flex-1 min-h-0' : 'min-h-[360px] max-h-[520px]'}`}>
            <div className="w-56 shrink-0 border-r border-gray-200 dark:border-gray-700 overflow-y-auto bg-gray-50 dark:bg-gray-900/50">
              {(() => {
                const tree = buildArtifactTree(artifactFiles);
                const renderNode = (node: ArtifactTreeNode, depth: number, keyPrefix: string) => {
                  const key = keyPrefix + node.name;
                  const isOpen = artifactTreeOpen[key] ?? (depth < 2);
                  const isFile = node.path != null;
                  return (
                    <div key={key} className="select-none">
                      {isFile ? (
                        <button
                          type="button"
                          onClick={() => setSelectedArtifactPath(node.path ?? null)}
                          className={`w-full text-left flex items-center gap-1.5 py-1.5 px-2 rounded text-sm truncate ${
                            selectedArtifactPath === node.path
                              ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300'
                              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                          }`}
                          style={{ paddingLeft: 8 + depth * 12 }}
                        >
                          <FileText className="h-4 w-4 shrink-0 text-gray-500 dark:text-gray-400" />
                          <span className="truncate">{node.name}</span>
                        </button>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => setArtifactTreeOpen((o) => ({ ...o, [key]: !isOpen }))}
                            className="w-full text-left flex items-center gap-1 py-1.5 px-2 rounded text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 truncate"
                            style={{ paddingLeft: 8 + depth * 12 }}
                          >
                            <span className="shrink-0 w-4 flex justify-center">
                              {isOpen ? (
                                <ChevronRight className="h-4 w-4 rotate-90" />
                              ) : (
                                <ChevronRight className="h-4 w-4" />
                              )}
                            </span>
                            {isOpen ? (
                              <FolderOpen className="h-4 w-4 shrink-0 text-amber-500 dark:text-amber-400" />
                            ) : (
                              <Folder className="h-4 w-4 shrink-0 text-amber-500 dark:text-amber-400" />
                            )}
                            <span className="truncate">{node.name}</span>
                          </button>
                          {isOpen && node.children?.map((child, i) => renderNode(child, depth + 1, `${key}/`))}
                        </>
                      )}
                    </div>
                  );
                };
                return tree.map((node, i) => renderNode(node, 0, `tree-${i}-`));
              })()}
            </div>
            <div className="flex-1 flex flex-col min-w-0 bg-white dark:bg-gray-800">
              {selectedArtifactPath ? (
                <>
                  <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400 font-mono truncate">
                    {selectedArtifactPath}
                  </div>
                  <div className="flex-1 overflow-auto p-3">
                    {artifactContentLoading ? (
                      <p className="text-sm text-gray-500 dark:text-gray-400">{t('common.loading')}</p>
                    ) : (
                      <textarea
                        readOnly
                        className="w-full h-full min-h-[280px] font-mono text-sm text-gray-800 dark:text-gray-200 bg-gray-50 dark:bg-gray-900 border-0 rounded p-2 resize-none focus:ring-0 focus:outline-none"
                        value={selectedArtifactContent ?? ''}
                        spellCheck={false}
                        data-gramm={false}
                      />
                    )}
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-sm text-gray-500 dark:text-gray-400">
                  {t('panel.jobDetailSelectFile')}
                </div>
              )}
            </div>
          </div>
          )}
        </div>
      )}

      {job.type === 'ai_translation' && job.aiPartialResults && Object.keys(job.aiPartialResults).length > 0 && (
        <div className={sectionShellClass(artifactsExpanded && artifactsOpen)}>
          <div className={`p-4 flex flex-wrap items-center gap-2 shrink-0 ${artifactsOpen ? 'border-b border-gray-200 dark:border-gray-700' : ''}`}>
            {renderSectionHeaderToggle('artifacts')}
            <FileText className="h-4 w-4 text-gray-500 dark:text-gray-400 shrink-0" />
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              {t('panel.jobDetailArtifacts')}
            </h2>
            <div className="ml-auto flex items-center gap-2">
              <button
                type="button"
                onClick={() => downloadJobArtifactArchive(job.id, 'localization-ios.zip', { format: 'ios' })}
                className="text-sm text-primary-600 dark:text-primary-400 hover:underline inline-flex items-center gap-1"
              >
                {t('panel.jobDetailDownloadZipIos')}
              </button>
              {artifactsOpen && renderExpandToggle('artifacts')}
            </div>
          </div>
          {artifactsOpen && (
          <div className={`flex ${artifactsExpanded ? 'flex-1 min-h-0' : 'min-h-[360px] max-h-[520px]'}`}>
            <div className="w-56 shrink-0 border-r border-gray-200 dark:border-gray-700 overflow-y-auto bg-gray-50 dark:bg-gray-900/50">
              {[...Object.keys(job.aiPartialResults)].sort((a, b) => a.localeCompare(b)).map((locale) => {
                const isSelected = selectedArtifactPath === locale;
                return (
                  <button
                    key={locale}
                    type="button"
                    onClick={() => setSelectedArtifactPath(locale)}
                    className={`w-full text-left flex items-center gap-1.5 py-1.5 px-2 rounded text-sm truncate ${
                      isSelected
                        ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                    }`}
                  >
                    <FileText className="h-4 w-4 shrink-0 text-gray-500 dark:text-gray-400" />
                    <span className="truncate" title={locale}>{locale}</span>
                  </button>
                );
              })}
            </div>
            <div className="flex-1 flex flex-col min-w-0 bg-white dark:bg-gray-800">
              {selectedArtifactPath && job.aiPartialResults[selectedArtifactPath] != null ? (
                <>
                  <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400 font-mono truncate flex items-center gap-2">
                    <FileText className="h-3.5 w-3.5 shrink-0" />
                    {selectedArtifactPath}
                  </div>
                  <div className="flex-1 overflow-auto p-3">
                    <textarea
                      readOnly
                      className="w-full h-full min-h-[280px] font-mono text-sm text-gray-800 dark:text-gray-200 bg-gray-50 dark:bg-gray-900 border-0 rounded p-2 resize-none focus:ring-0 focus:outline-none cursor-default"
                      value={job.aiPartialResults[selectedArtifactPath] ?? ''}
                      spellCheck={false}
                      data-gramm={false}
                      title={t('panel.jobDetailReadOnly')}
                    />
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-sm text-gray-500 dark:text-gray-400">
                  {t('panel.jobDetailSelectFile')}
                </div>
              )}
            </div>
          </div>
          )}
        </div>
      )}

      {artifactFolders.length > 0 &&
        job.type !== 'metadata_export' &&
        job.type !== 'metadata_upload' &&
        job.type !== 'ai_translation' && (
        <div className={sectionShellClass(artifactsExpanded && artifactsOpen)}>
          <div className={`p-4 flex flex-wrap items-center gap-2 shrink-0 ${artifactsOpen ? 'border-b border-gray-200 dark:border-gray-700' : ''}`}>
            {renderSectionHeaderToggle('artifacts')}
            <FileImage className="h-4 w-4 text-gray-500 dark:text-gray-400 shrink-0" />
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              {t('panel.jobDetailArtifacts')}
            </h2>
            <div className="ml-auto flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => downloadJobArtifactArchive(job.id)}
                className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
              >
                {t('panel.jobDetailDownloadZip')}
              </button>
              {artifactsOpen && renderExpandToggle('artifacts')}
            </div>
          </div>
          {artifactsOpen && (
          <div className={`flex ${artifactsExpanded ? 'flex-1 min-h-0' : 'min-h-[360px] max-h-[520px]'}`}>
            {(() => {
              const metadataAndroidLocales = buildMetadataAndroidLocales(artifactFolders);
              const useLocaleList = metadataAndroidLocales.length > 0;
              const effectiveTab = useLocaleList
                ? (metadataAndroidLocales.some((e) => e.locale === artifactTab) ? artifactTab : metadataAndroidLocales[0]?.locale ?? '')
                : artifactTab;
              const pathPrefix = useLocaleList ? 'metadata/' : (artifactFolders.find((f) => (f.name || '') === effectiveTab)?.name ? `${effectiveTab}/` : (artifactFolders[0]?.name ? `${artifactFolders[0].name}/` : ''));
              const currentEntry = useLocaleList
                ? metadataAndroidLocales.find((e) => e.locale === effectiveTab)
                : null;
              const currentFiles = useLocaleList && currentEntry
                ? currentEntry.files
                : (artifactFolders.find((f) => (f.name || '') === effectiveTab) ?? artifactFolders[0])?.files ?? [];
              const folderLabel = useLocaleList ? effectiveTab : (artifactFolders.find((f) => (f.name || '') === effectiveTab)?.name ?? artifactFolders[0]?.name ?? t(getArtifactTabLabelKey(job.type)));

              return (
                <>
                  <div className="w-56 shrink-0 border-r border-gray-200 dark:border-gray-700 overflow-y-auto bg-gray-50 dark:bg-gray-900/50 flex flex-col">
                    <div className="px-3 py-2.5 border-b border-gray-200 dark:border-gray-700 shrink-0">
                      <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        {t('panel.jobDetailMetadata')}
                      </h3>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                        {t('panel.jobDetailLocalizations')}
                      </p>
                    </div>
                    <div className="flex-1 overflow-y-auto py-1">
                      {useLocaleList ? (
                        metadataAndroidLocales.map(({ locale, files: fileList }) => {
                          const isActive = effectiveTab === locale;
                          return (
                            <button
                              key={locale}
                              type="button"
                              onClick={() => setArtifactTab(locale)}
                              className={`w-full text-left flex items-center gap-1.5 py-2 px-3 rounded-none border-l-2 text-sm font-medium transition-colors ${
                                isActive
                                  ? 'border-primary-600 bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                                  : 'border-transparent text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                              }`}
                            >
                              {isActive ? (
                                <FolderOpen className="h-4 w-4 shrink-0 text-amber-500 dark:text-amber-400" />
                              ) : (
                                <Folder className="h-4 w-4 shrink-0 text-amber-500 dark:text-amber-400" />
                              )}
                              <span className="truncate flex-1" title={locale}>{locale}</span>
                              <span className="text-xs text-gray-400 dark:text-gray-500 tabular-nums shrink-0">
                                {fileList.length}
                              </span>
                            </button>
                          );
                        })
                      ) : (
                        artifactFolders.map((folder) => {
                          const folderKey = folder.name || '';
                          const label = folder.name || t(getArtifactTabLabelKey(job.type));
                          const isActive = artifactTab === folderKey;
                          return (
                            <button
                              key={folderKey || 'root'}
                              type="button"
                              onClick={() => setArtifactTab(folderKey)}
                              className={`w-full text-left flex items-center gap-1.5 py-2 px-3 rounded-none border-l-2 text-sm font-medium transition-colors ${
                                isActive
                                  ? 'border-primary-600 bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                                  : 'border-transparent text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                              }`}
                            >
                              {isActive ? (
                                <FolderOpen className="h-4 w-4 shrink-0 text-amber-500 dark:text-amber-400" />
                              ) : (
                                <Folder className="h-4 w-4 shrink-0 text-amber-500 dark:text-amber-400" />
                              )}
                              <span className="truncate flex-1" title={label}>{label}</span>
                              <span className="text-xs text-gray-400 dark:text-gray-500 tabular-nums shrink-0">
                                {folder.files.length}
                              </span>
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>
                  <div className="flex-1 flex flex-col min-w-0 overflow-auto bg-white dark:bg-gray-800">
                    <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400 flex items-center gap-2 shrink-0">
                      <FolderOpen className="h-3.5 w-3.5 shrink-0" />
                      <span className="truncate">{folderLabel}</span>
                      <span className="tabular-nums">{currentFiles.length} {t('panel.jobDetailFiles')}</span>
                    </div>
                    <div className="flex-1 p-4 overflow-auto">
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                        {currentFiles.map((fileSuffix) => {
                          const path = pathPrefix + fileSuffix;
                          const filename = fileSuffix.split('/').pop() ?? fileSuffix;
                          const url = getJobArtifactUrl(job.id, path);
                          const isImage = /\.(jpe?g|png|gif|webp)$/i.test(filename);
                          return (
                            <div
                              key={path}
                              className="rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900 overflow-hidden shrink-0 w-[7rem] mx-auto"
                            >
                              {isImage ? (
                                <button
                                  type="button"
                                  onClick={() => setLightboxImage({ jobId: job.id, path, filename })}
                                  className="block w-full aspect-[9/19] bg-gray-100 dark:bg-gray-800 cursor-pointer hover:ring-2 hover:ring-primary-500 rounded-t-lg overflow-hidden focus:outline-none focus:ring-2 focus:ring-primary-500"
                                >
                                  <ArtifactImage
                                    jobId={job.id}
                                    path={path}
                                    filename={filename}
                                    className="w-full h-full object-cover"
                                  />
                                </button>
                              ) : (
                                <a
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-2 p-2 text-sm text-primary-600 dark:text-primary-400 hover:underline"
                                >
                                  <FileImage className="h-4 w-4 shrink-0" />
                                  <span className="truncate">{filename}</span>
                                </a>
                              )}
                              <div className="px-2 py-1 border-t border-gray-200 dark:border-gray-600">
                                <span className="text-xs text-gray-500 dark:text-gray-400 truncate block" title={filename}>
                                  {filename}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
          )}
        </div>
      )}

      </div>
    </div>
    {lightboxImage && (
      <ArtifactImageLightbox
        jobId={lightboxImage.jobId}
        path={lightboxImage.path}
        filename={lightboxImage.filename}
        onClose={() => setLightboxImage(null)}
      />
    )}
    <ConfirmDialog
      open={deleteConfirmOpen}
      onClose={() => {
        if (!deleting) setDeleteConfirmOpen(false);
      }}
      title={t('panel.deleteJobTitle')}
      message={t('panel.confirmDeleteJob', { type: jobTypeLabel })}
      confirmLabel={deleting ? t('panel.deleting') : t('common.delete')}
      cancelLabel={t('common.cancel')}
      onConfirm={() => void confirmDelete()}
      confirmLoading={deleting}
      closeDisabled={deleting}
      variant="danger"
    />
    </>
  );
};

export default JobDetail;
